import { storage } from './utils';

export type Theme = 'light' | 'dark' | 'system';

const THEME_KEY = 'ivina-theme';

/**
 * Get the current theme from localStorage or system preference
 *
 * NOTE: Currently defaults to 'light' because the canvas (React Flow editor)
 * is not yet dark mode ready. Before enabling 'system' as the default,
 * the canvas backgrounds, nodes, edges, and controls need to be updated
 * to support dark mode styling.
 */
export function getTheme(): Theme {
  const stored = storage.get(THEME_KEY) as Theme | null;
  if (stored && ['light', 'dark', 'system'].includes(stored)) {
    return stored;
  }
  return 'light';
}

/**
 * Set the theme and update the document
 */
export function setTheme(theme: Theme): void {
  storage.set(THEME_KEY, theme);
  applyTheme(theme);
}

/**
 * Apply theme to document
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  
  // Remove existing theme
  root.removeAttribute('data-theme');
  
  if (theme === 'system') {
    // Check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      root.setAttribute('data-theme', 'dark');
    }
  } else if (theme === 'dark') {
    root.setAttribute('data-theme', 'dark');
  }
  // Light theme is the default (no data-theme attribute)
}

/**
 * Initialize theme on app load
 */
export function initializeTheme(): void {
  const theme = getTheme();
  applyTheme(theme);
  
  // Listen for system theme changes if using system theme
  if (theme === 'system') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      const currentTheme = getTheme();
      if (currentTheme === 'system') {
        applyTheme('system');
      }
    });
  }
}

/**
 * Get the effective theme (resolved system theme)
 */
export function getEffectiveTheme(): 'light' | 'dark' {
  const theme = getTheme();
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

/**
 * Toggle between light and dark themes
 */
export function toggleTheme(): void {
  const current = getEffectiveTheme();
  setTheme(current === 'light' ? 'dark' : 'light');
}

/**
 * React hook for theme management
 */
export function useTheme() {
  const [theme, setThemeState] = React.useState<Theme>(getTheme);
  const [effectiveTheme, setEffectiveTheme] = React.useState<'light' | 'dark'>(
    getEffectiveTheme
  );
  
  React.useEffect(() => {
    // Update effective theme when theme changes
    setEffectiveTheme(getEffectiveTheme());
    
    // Listen for storage events (theme changed in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_KEY) {
        const newTheme = (e.newValue as Theme) || 'system';
        setThemeState(newTheme);
        applyTheme(newTheme);
        setEffectiveTheme(getEffectiveTheme());
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (theme === 'system') {
        applyTheme('system');
        setEffectiveTheme(getEffectiveTheme());
      }
    };
    
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [theme]);
  
  const updateTheme = React.useCallback((newTheme: Theme) => {
    setTheme(newTheme);
    setThemeState(newTheme);
    setEffectiveTheme(getEffectiveTheme());
  }, []);
  
  return {
    theme,
    effectiveTheme,
    setTheme: updateTheme,
    toggleTheme,
  };
}

// Import React at the end to avoid circular dependencies
import * as React from 'react';
